import { FilterUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Variant-level filtering for variant grid collection pages.
 * Intercepts availability filter changes and applies variant-level filtering
 * instead of product-level filtering.
 */
class VariantFilter {
  constructor() {
    this.variantGridSection = null;
    this.observer = null;
    this.init();
  }

  /**
   * Initialize the variant filter
   */
  init() {
    // Find the variant grid section
    this.variantGridSection = document.querySelector('.variant-product-grid-section');
    
    if (!this.variantGridSection) {
      // Not on a variant grid page, exit
      return;
    }

    // Listen for filter update events
    document.addEventListener(ThemeEvents.FilterUpdate, this.handleFilterUpdate.bind(this));

    // Use MutationObserver to detect when grid is updated via AJAX
    this.observer = new MutationObserver(() => {
      // Debounce the filter application
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyVariantFiltering();
        this.updateFilterCounts();
      }, 300);
    });

    // Observe the product grid for changes
    const grid = this.variantGridSection.querySelector('.product-grid');
    if (grid) {
      this.observer.observe(grid, {
        childList: true,
        subtree: true,
      });
    }

    // Initial application
    this.applyVariantFiltering();
    this.updateFilterCounts();
  }

  /**
   * Handle filter update events
   * @param {FilterUpdateEvent} event
   */
  handleFilterUpdate(event) {
    // Wait for DOM to update, then apply variant filtering
    setTimeout(() => {
      this.applyVariantFiltering();
      this.updateFilterCounts();
    }, 100);
  }

  /**
   * Get the active availability filter value from URL or active filters
   * @returns {string|null} '1' for in stock, '0' for out of stock, null if no filter
   */
  getAvailabilityFilterValue() {
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const availabilityParam = urlParams.get('filter.v.availability');
    
    if (availabilityParam !== null) {
      return availabilityParam;
    }

    // Check active filter pills/tags
    const activeFilters = this.variantGridSection.querySelectorAll('.facets__active-filters [data-filter-value]');
    for (const filter of activeFilters) {
      const label = filter.textContent.toLowerCase();
      if (label.includes('availability') || label.includes('stock')) {
        const value = filter.getAttribute('data-filter-value');
        if (value === '1' || value === '0') {
          return value;
        }
      }
    }

    // Check form checkboxes
    const availabilityCheckboxes = this.variantGridSection.querySelectorAll(
      'input[type="checkbox"][name*="availability"]:checked'
    );
    if (availabilityCheckboxes.length > 0) {
      return availabilityCheckboxes[0].value;
    }

    return null;
  }

  /**
   * Apply variant-level availability filtering
   */
  applyVariantFiltering() {
    const filterValue = this.getAvailabilityFilterValue();
    
    if (filterValue === null) {
      // No availability filter active, show all variants
      const allVariants = this.variantGridSection.querySelectorAll('.variant-grid__item');
      allVariants.forEach((item) => {
        item.style.display = '';
      });
      return;
    }

    // Filter variants based on availability
    const variantItems = this.variantGridSection.querySelectorAll('.variant-grid__item');
    variantItems.forEach((item) => {
      const isAvailable = item.getAttribute('data-variant-available') === 'true';
      
      if (filterValue === '1') {
        // Show only in-stock variants
        item.style.display = isAvailable ? '' : 'none';
      } else if (filterValue === '0') {
        // Show only out-of-stock variants
        item.style.display = isAvailable ? 'none' : '';
      }
    });
  }

  /**
   * Update filter counts to show variant counts instead of product counts
   */
  updateFilterCounts() {
    // Find all availability filter labels
    const filterLabels = this.variantGridSection.querySelectorAll(
      '.facets__label, .facets__summary, [data-filter-label]'
    );

    filterLabels.forEach((label) => {
      const labelText = label.textContent.toLowerCase();
      
      // Check if this is an availability filter
      if (!labelText.includes('availability') && !labelText.includes('stock')) {
        return;
      }

      // Count variants instead of products
      const variantItems = this.variantGridSection.querySelectorAll('.variant-grid__item');
      const inStockCount = Array.from(variantItems).filter(
        (item) => item.getAttribute('data-variant-available') === 'true'
      ).length;
      const outOfStockCount = Array.from(variantItems).filter(
        (item) => item.getAttribute('data-variant-available') === 'false'
      ).length;

      // Update label text if it contains a count
      const currentText = label.textContent;
      const match = currentText.match(/\((\d+)\)/);
      
      if (match) {
        // Determine which count to show based on filter value
        const filterValue = this.getAvailabilityFilterValue();
        let count = 0;
        
        if (filterValue === '1') {
          count = inStockCount;
        } else if (filterValue === '0') {
          count = outOfStockCount;
        } else {
          // Show both counts if no filter is active
          // This is a simplified approach - you may want to enhance this
          return;
        }

        // Update the count in the label
        const newText = currentText.replace(/\(\d+\)/, `(${count})`);
        if (newText !== currentText) {
          label.textContent = newText;
        }
      }
    });
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.handleFilterUpdate);
    clearTimeout(this.filterTimeout);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantFilter();
  });
} else {
  new VariantFilter();
}
